import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import Option from "../types/option";
import Dashboard from "../types/dashboard";

interface DashboardState {
  status: "idle" | "loading" | "failed";
  homeDashboard?: Dashboard;
  selectedDashboard?: number;
  dashboardOptions?: Option[];
  currentDashboard?: Dashboard;
}

const initialState: DashboardState = {
  status: "idle",
};

export const getDashboardList = createAsyncThunk<Option[]>(
  "dashboard/fetch-list",
  async () => {
    const response = await http.get<Option[]>(`/dashboard`);
    return response.data;
  }
);

export const getDashboardDetail = createAsyncThunk<Dashboard, number>(
  "dashboard/fetch-detail",
  async (id: number) => {
    const response = await http.get<Dashboard>(`/dashboard/${id}`);
    return response.data;
  }
);

export const getHomeDashboard = createAsyncThunk<Dashboard>(
  "dashboard/fetch-home",
  async () => {
    const response = await http.get<Dashboard>(`/dashboard/home`);
    return response.data;
  }
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    setCurrentDashboard(state, action) {
      state.currentDashboard = action.payload;
    },
    setSelectedDashboard(state, action) {
      state.selectedDashboard = action.payload || undefined;
    },
    clearDashboardList(state) {
      state.dashboardOptions = initialState.dashboardOptions;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDashboardList.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getDashboardList.fulfilled, (state, action) => {
        state.status = "idle";
        state.dashboardOptions = action.payload;
      })
      .addCase(getDashboardList.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(getDashboardDetail.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getDashboardDetail.fulfilled, (state, action) => {
        state.status = "idle";
        state.currentDashboard = action.payload;
      })
      .addCase(getDashboardDetail.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(getHomeDashboard.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getHomeDashboard.fulfilled, (state, action) => {
        state.status = "idle";
        state.homeDashboard = action.payload ?? undefined;
      })
      .addCase(getHomeDashboard.rejected, (state, action) => {
        state.status = "failed";
      });
  },
});

export const { setCurrentDashboard, setSelectedDashboard, clearDashboardList } =
  dashboardSlice.actions;

export default dashboardSlice.reducer;
